import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaMapMarkedAlt, FaSpinner, FaExclamationCircle, FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import { fetchGpsHistory, fetchYardDevices } from '../services/gpsHistory.service';
import { usePortalAccess } from '../context/PortalAccessContext';
import { TravelMapView, MapLegend, HOUR_OPTIONS, getDeviceColor } from '../components/gps-travel-map';
import { probeYardTier1 } from '../components/yard-layout/probeYardTier1';

const LIVE_POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes — auto-update last location trails

/**
 * Yard Travel Map page: pick yard, time range, add devices, show GPS paths on map.
 * "Show last location" runs in live mode: poll for new points and connect with polylines.
 */
export default function YardTravelMapPage() {
  const { access } = usePortalAccess();
  const portalEmail = (access?.email || '').trim();
  const [yardNumber, setYardNumber] = useState('');
  const [hours, setHours] = useState(12);
  const [yardDevices, setYardDevices] = useState([]);
  const [yardLoading, setYardLoading] = useState(false);
  const [yardError, setYardError] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState('');

  const [selectedAndroidIds, setSelectedAndroidIds] = useState([]);
  const [paths, setPaths] = useState([]);
  const [pathsLoading, setPathsLoading] = useState(false);
  const [pathsError, setPathsError] = useState(null);
  const [pathsEmptyMessage, setPathsEmptyMessage] = useState(null);

  const [yardTileUrl, setYardTileUrl] = useState(null);

  const liveIntervalRef = useRef(null);
  const isLiveModeRef = useRef(false);
  const liveConfigRef = useRef({ androidIds: [], yard: undefined });

  useEffect(() => {
    const yard = yardNumber.trim();
    if (!yard) { setYardTileUrl(null); return; }
    let cancelled = false;
    const controller = new AbortController();
    probeYardTier1(yard, { signal: controller.signal }).then((available) => {
      if (cancelled) return;
      setYardTileUrl(
        available
          ? `https://yardimages.copart.com/styles/yard${yard}-lite/style.json`
          : null
      );
    });
    return () => { cancelled = true; controller.abort(); };
  }, [yardNumber]);

  const stopLivePolling = useCallback(() => {
    isLiveModeRef.current = false;
    if (liveIntervalRef.current != null) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
  }, []);

  const loadYardDevices = useCallback(async () => {
    const yard = yardNumber.trim();
    if (!yard) {
      setYardDevices([]);
      return;
    }
    stopLivePolling();
    setYardLoading(true);
    setYardError(null);
    try {
      const data = await fetchYardDevices(yard, '30 days', portalEmail);
      setYardDevices(data.devices || []);
      setSelectedAndroidIds([]);
      setPaths([]);
    } catch (err) {
      setYardError(err.message || 'Failed to load yard devices');
      setYardDevices([]);
    } finally {
      setYardLoading(false);
    }
  }, [yardNumber, stopLivePolling, portalEmail]);

  useEffect(() => {
    const t = setTimeout(loadYardDevices, 300);
    return () => clearTimeout(t);
  }, [loadYardDevices]);

  useEffect(() => {
    return () => {
      if (liveIntervalRef.current != null) clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
      isLiveModeRef.current = false;
    };
  }, []);

  const toggleDevice = (androidId) => {
    setSelectedAndroidIds((prev) =>
      prev.includes(androidId)
        ? prev.filter((id) => id !== androidId)
        : [...prev, androidId]
    );
  };

  const loadPaths = async () => {
    if (selectedAndroidIds.length === 0) {
      setPaths([]);
      setPathsError(null);
      return;
    }
    stopLivePolling();
    setPathsLoading(true);
    setPathsError(null);
    setPathsEmptyMessage(null);
    setPaths([]);
    try {
      const data = await fetchGpsHistory(selectedAndroidIds, hours, undefined, portalEmail);
      const points = Array.isArray(data?.points) ? data.points : [];
      if (points.length === 0) {
        setPathsEmptyMessage('No GPS data for selected devices in this time range.');
        setPaths([]);
        return;
      }
      const byDevice = {};
      points.forEach((p) => {
        if (p == null || p.android_id == null) return;
        const lat = p.latitude != null ? Number(p.latitude) : null;
        const lng = p.longitude != null ? Number(p.longitude) : null;
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
        const id = String(p.android_id);
        if (!byDevice[id]) byDevice[id] = [];
        byDevice[id].push({ ...p, latitude: lat, longitude: lng });
      });
      const deviceNames = {};
      points.forEach((p) => {
        if (p?.android_id != null && !deviceNames[p.android_id])
          deviceNames[p.android_id] = p.device_friendly_name || p.android_id;
      });
      const pathList = selectedAndroidIds
        .filter((id) => (byDevice[id] || []).length > 0)
        .map((id, index) => {
          const raw = (byDevice[id] || []).sort(
            (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
          );
          return {
            deviceId: id,
            deviceName: deviceNames[id] || id,
            color: getDeviceColor(index),
            points: raw,
          };
        });
      setPaths(pathList);
      if (pathList.length === 0)
        setPathsEmptyMessage('No GPS data for selected devices in this time range.');
    } catch (err) {
      setPathsError(err?.message || 'Failed to load GPS history');
      setPaths([]);
    } finally {
      setPathsLoading(false);
    }
  };

  // Poll: fetch latest points and append newer ones to each device's trail (polyline grows)
  const pollLastLocations = useCallback(async () => {
    if (!isLiveModeRef.current) return;
    const { androidIds, yard } = liveConfigRef.current;
    if (!androidIds || androidIds.length === 0) return;
    try {
      const data = await fetchGpsHistory(androidIds, 24, yard, portalEmail);
      const points = Array.isArray(data?.points) ? data.points : [];
      if (points.length === 0) return;
      const byDevice = {};
      points.forEach((p) => {
        if (p == null || p.android_id == null) return;
        const lat = p.latitude != null ? Number(p.latitude) : null;
        const lng = p.longitude != null ? Number(p.longitude) : null;
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
        const id = String(p.android_id);
        if (!byDevice[id]) byDevice[id] = [];
        byDevice[id].push({ ...p, latitude: lat, longitude: lng });
      });
      setPaths((prevPaths) => {
        if (prevPaths.length === 0) return prevPaths;
        return prevPaths.map((path) => {
          const id = path.deviceId;
          const devicePoints = (byDevice[id] || []).sort(
            (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
          );
          const latestNew = devicePoints[0];
          if (!latestNew) return path;
          const currentLast = path.points[path.points.length - 1];
          const isNewer =
            !currentLast ||
            new Date(latestNew.timestamp) > new Date(currentLast.timestamp);
          if (!isNewer) return path;
          const nextPoints = [...path.points, latestNew].sort(
            (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
          );
          return { ...path, points: nextPoints };
        });
      });
    } catch (_) {
      // Keep current paths; retry on next poll
    }
  }, [portalEmail]);

  // Show last location: one pulsating marker per device; then poll and append newer points (polyline per device)
  const loadLastLocations = async () => {
    const yard = yardNumber.trim();
    const androidIds =
      selectedAndroidIds.length > 0
        ? selectedAndroidIds
        : yard && yardDevices.length > 0
          ? yardDevices.map((d) => d.android_id).filter(Boolean)
          : [];
    if (androidIds.length === 0) {
      setPathsEmptyMessage('Enter a yard number (and wait for devices) or select one or more devices.');
      setPaths([]);
      return;
    }
    stopLivePolling();
    setPathsLoading(true);
    setPathsError(null);
    setPathsEmptyMessage(null);
    setPaths([]);
    try {
      const data = await fetchGpsHistory(androidIds, 24, undefined, portalEmail);
      const points = Array.isArray(data?.points) ? data.points : [];
      if (points.length === 0) {
        setPathsEmptyMessage('No GPS data for selected devices.');
        setPaths([]);
        return;
      }
      const byDevice = {};
      points.forEach((p) => {
        if (p == null || p.android_id == null) return;
        const lat = p.latitude != null ? Number(p.latitude) : null;
        const lng = p.longitude != null ? Number(p.longitude) : null;
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
        const id = String(p.android_id);
        if (!byDevice[id]) byDevice[id] = [];
        byDevice[id].push({ ...p, latitude: lat, longitude: lng });
      });
      const deviceNames = {};
      points.forEach((p) => {
        if (p?.android_id != null && !deviceNames[p.android_id])
          deviceNames[p.android_id] = p.device_friendly_name || p.android_id;
      });
      const pathList = androidIds
        .filter((id) => (byDevice[id] || []).length > 0)
        .map((id, index) => {
          const raw = (byDevice[id] || []).sort(
            (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
          );
          const latest = raw[0];
          return {
            deviceId: id,
            deviceName: deviceNames[id] || id,
            color: getDeviceColor(index),
            points: latest ? [latest] : [],
          };
        })
        .filter((p) => p.points.length > 0);
      setPaths(pathList);
      if (pathList.length === 0) {
        setPathsEmptyMessage('No GPS data for selected devices.');
      } else {
        isLiveModeRef.current = true;
        liveConfigRef.current = { androidIds, yard: undefined };
        if (liveIntervalRef.current != null) clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = setInterval(pollLastLocations, LIVE_POLL_INTERVAL_MS);
      }
    } catch (err) {
      setPathsError(err?.message || 'Failed to load last locations');
      setPaths([]);
    } finally {
      setPathsLoading(false);
    }
  };

  const canShowLastLocation =
    (yardNumber.trim() && yardDevices.length > 0) || selectedAndroidIds.length > 0;

  const mapPlaceholderText = pathsEmptyMessage || 'Select devices and click "Show on map" or "Show last location"';

  // Filter devices by search (device name or android_id)
  const filteredYardDevices = useMemo(() => {
    const q = (deviceSearch || '').trim().toLowerCase();
    if (!q) return yardDevices;
    return yardDevices.filter((d) => {
      const name = (d.device_friendly_name || d.device_name || '').toLowerCase();
      const id = (d.android_id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [yardDevices, deviceSearch]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="w-full">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaMapMarkedAlt />
            Yard Travel Map
          </h1>
          <p className="text-gray-600 mt-1">
            View where devices traveled (GPS every ~2 min). Pick a yard, add devices, choose time range.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls */}
          <div className="lg:col-span-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yard number</label>
              <input
                type="text"
                value={yardNumber}
                onChange={(e) => setYardNumber(e.target.value)}
                placeholder="e.g. 207"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time range</label>
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {yardError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <FaExclamationCircle />
                {yardError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Devices in yard</label>
              {yardDevices.length > 0 && (
                <div className="relative mb-2">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    placeholder="Search by device name or ID"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
              {yardLoading ? (
                <div className="flex items-center gap-2 text-gray-500 py-4">
                  <FaSpinner className="animate-spin" />
                  Loading devices…
                </div>
              ) : yardDevices.length === 0 ? (
                <p className="text-gray-500 text-sm py-2">
                  Enter a yard number to list devices.
                </p>
              ) : filteredYardDevices.length === 0 ? (
                <p className="text-gray-500 text-sm py-2">
                  No devices match &quot;{deviceSearch.trim()}&quot;.
                </p>
              ) : (
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {filteredYardDevices.map((d) => (
                    <li key={d.android_id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedAndroidIds.includes(d.android_id)}
                        onChange={() => toggleDevice(d.android_id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm truncate">
                        {d.device_friendly_name || d.android_id}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={loadPaths}
              disabled={selectedAndroidIds.length === 0 || pathsLoading}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {pathsLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Loading…
                </>
              ) : (
                'Show on map'
              )}
            </button>
            <button
              onClick={loadLastLocations}
              disabled={!canShowLastLocation || pathsLoading}
              className="w-full py-2 px-4 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {pathsLoading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaMapMarkerAlt />
              )}
              Show last location
            </button>
            {pathsError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <FaExclamationCircle />
                {pathsError}
              </div>
            )}
            {pathsEmptyMessage && !pathsError && (
              <div className="text-amber-700 text-sm bg-amber-50 rounded-lg px-3 py-2">
                {pathsEmptyMessage}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="lg:col-span-2 relative">
            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm" style={{ minHeight: 450 }}>
              <TravelMapView paths={paths} yardTileUrl={yardTileUrl} />
              <MapLegend paths={(paths || []).map((p) => ({ deviceName: p?.deviceName ?? '', color: p?.color ?? '#666' }))} />
            </div>
            {paths.length === 0 && !pathsLoading && (
              <p className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">
                {mapPlaceholderText}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
